import type { CandidateFinding } from './finding';
import type { Severity } from './enums';

/**
 * The sentences and markers a **client or a reviewer** reads about a finding —
 * kept here because here is the only place they can be shared
 * (docs/DECISIONS.md ZD-112).
 *
 * ## Why this module exists at all
 *
 * `sds-12b` needed the report's vocabulary in a browser, and the obvious route
 * — `apps/web` importing `@zevik/report` — was measured rather than assumed. It
 * pulls `@zevik/packs` entire: **303 `qrsctl_` occurrences, 143
 * `threat_pattern_` ids, all three domain packs, 66 `conditional_guidance` and
 * 67 `evidence_requirements` strings** (the two fields `sds-12f` is scheduled
 * to declare as wholly internal), `mapping_rationale`, and **28 `ZD-0`
 * citations** inside runtime strings, into a bundle a browser serves. Counted in
 * the shipped minified output of a real `vite build`, not inferred: 532.08 kB
 * against a 193.66 kB baseline, 142.29 kB gzip against 60.89 kB, 166 modules
 * against 29.
 *
 * **The boundary is structural rather than a bundler setting, and that is the
 * part worth knowing before reaching for a config change.** `@zevik/packs`
 * declares only `.` in its `exports`, so every import resolves through its
 * `index.ts`, whose `PACKS` is initialised at module load by
 * `ThreatPackSchema.parse(pack)` — a side-effectful call no bundler can prove
 * pure, so tree-shaking cannot drop it and no `manualChunks` or `sideEffects`
 * setting changes that. The package's own docstring says publishing pack
 * content is *"a deliberate decision, never a convenience"*, and CI already runs
 * `pnpm --filter @zevik/web run build`.
 *
 * ## What is here and what deliberately is not
 *
 * Here: the two rules a second surface would otherwise re-express, and the one
 * sentence that is not written about a report. `@zevik/report` **re-exports
 * every one of them and consumes them in `renderReport`**, so there is one
 * derivation with two consumers rather than two expressions that agree today
 * (ZD-017, ZD-027 rule 3).
 *
 * Not here, and the split is reasoned rather than lazy: `COMPLETENESS_LIMIT`
 * and `PROVENANCE_LIMITS` both say *"in this report"*, so they are claims about
 * a document and cannot be carried verbatim onto a screen. A reviewer's
 * equivalent of the second is per **finding** and already computable —
 * `statementProseOrigin` — which is a different claim, not a copy of that one.
 * `PROPORTIONALITY_NOTE` is about the report's own proportionality. All three
 * stay in `@zevik/report`; moving them would put a report's sentence somewhere
 * a non-report surface could reach for it.
 */

/**
 * The band to show, or `null` where none may be shown.
 *
 * **The rule, not the formatting.** `renderReport` renders the return value as
 * `` ` — ${band}` `` under an object's bullet and a reviewer workspace renders
 * it as a chip; those are presentation. What must not differ is *whether there
 * is a band at all*, because a surface that prints `low` where the report prints
 * nothing has invented a severity for a finding nothing scored.
 *
 * `unscored` is the one method with no band to report. It is a real state rather
 * than a defensive branch: `scoring_method` is an enum the pipeline writes, and
 * a finding whose lens declares no dimensions this method reads has nothing to
 * band (ZD-058 §2).
 */
export function reportedBand(
  finding: Pick<CandidateFinding, 'scoring_method' | 'severity_normalised'>,
): Severity | null {
  return finding.scoring_method === 'unscored' ? null : finding.severity_normalised;
}

/**
 * The marker in front of a finding whose verdict nobody has settled.
 *
 * Carried as the words rather than as a boolean because the words are the shared
 * thing: a reviewer told `medium` where the report says `medium if confirmed` is
 * reviewing a different object, and two surfaces choosing their own phrase for
 * that is how the two come apart.
 *
 * **The standing leads, and that is part of the rule.** A pattern's label is
 * written as an assertion — *"A path into a data store is stated to be
 * unencrypted"* — so printing the label first under a finding that is explicitly
 * unresolved makes the assertion the thing a skimming reader takes away. Both
 * surfaces put this before the label.
 */
export const CONDITIONAL_PREFIX = 'If confirmed:';

/**
 * What the marker means, without the clause that says where to look.
 *
 * The locating half is per surface — a report says *"the question is in the next
 * section"*, a workspace says where its own question list is — and it is the
 * only half that may differ. Splitting there rather than sharing the whole
 * sentence is what keeps a document's structure out of a screen's copy while
 * still leaving one statement of what the marker asserts.
 */
export const CONDITIONAL_MARKER_MEANING =
  'rests on a question our analysis could not settle: it is here because your answer decides it';

/**
 * What a severity band means, said where a client reads it (ZD-058 §5).
 *
 * **A band is the most quotable thing this product emits and the easiest to
 * over-read**, and the specific over-reading is measurable: a band is largely a
 * property of the *threat class* rather than of the client's system, because
 * only one of the nine dimensions has a source in their model at all. Two
 * different systems where the same pattern fires will usually carry the same
 * band. So the sentence says what it is a function of rather than asking a
 * reader to discount it.
 *
 * **It carries no figure**, and that is deliberate: every number available here
 * is measured against four invented systems, and a client's own is not in the
 * denominator, so publishing one would be a claim about a different system
 * (ZD-057 §6). What is client-safe is the *shape* — the same device that
 * sentence used for the question count.
 *
 * The second half is the one a reader needs most and is the commonest case:
 * where the documents did not establish what data a threat touches, the band is
 * computed without it. That is stated per finding as well, next to the band, so
 * it is not a preamble doing the work.
 *
 * It moved here from `@zevik/report` in `sds-12b` and is re-exported from there,
 * because it is the one of the four limit sentences that is not a claim about a
 * report — it is a claim about a band, and a reviewer reads bands too.
 */
export const SEVERITY_LIMIT =
  'A severity band says how much a threat of this kind would matter if it holds, given what ' +
  'our packs express about it and what your documents established. It is not a probability, ' +
  'not a financial estimate, and not a rating of your system as a whole — two systems where ' +
  'the same threat applies will often carry the same band. Where your documents did not ' +
  'establish what data a threat touches, the band is worked out without that, and the finding ' +
  'says so.';
